import type { Task, UpdateTaskRequest } from '@afra/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { RequestError, api } from '../../lib/api'
import { TaskDue, VERDICT_FA } from '../chat/Rail'
import { faNum } from '../platform/labels'

/**
 * «برنامه‌ریزی اقدامات» — the tasks the agent and the organization agreed on,
 * as a plan the organization runs.
 *
 * Three columns, as Afra's prototype draws them: waiting, under way, done.
 * Moving a task between the first two is the organization's to do. Moving one
 * into the third is not: there is no «done» button here any more than in the
 * chat panel. The organization writes what changed and the judge decides,
 * against the acceptance test written when the task was made.
 *
 * Every change made here reaches the agent on its next turn, from the same
 * rows, so the plan and the conversation never disagree.
 */
export function PlanningPage() {
  const tasks = useQuery({ queryKey: ['tasks'], queryFn: api.tasks })
  const [showSetAside, setShowSetAside] = useState(false)

  if (tasks.isLoading || !tasks.data) {
    return (
      <div className="container center">
        <p className="muted">در حال بارگذاری…</p>
      </div>
    )
  }

  const all = tasks.data.tasks
  const waiting = all.filter((t) => t.status === 'open' && t.progress === 'todo')
  const underway = all.filter((t) => t.status === 'open' && t.progress === 'doing')
  const done = all.filter((t) => t.status === 'done')
  const setAside = all.filter((t) => t.status === 'abandoned')
  const late = all.filter((t) => t.status === 'open' && t.timing === 'overdue').length
  const open = waiting.length + underway.length

  return (
    <div className="container container--wide stack">
      <div className="card">
        <h1>برنامه‌ریزی اقدامات</h1>
        <p className="meta">
          کارهایی که در گفت‌وگو با افرا قرار شد انجام بدهید. هر کار یک «نشانه انجام شدن» دارد و وقتی
          بسته می‌شود که گزارش انجامش را بفرستید و با همان نشانه بخواند.
        </p>
        <div className="stats">
          <div className="stat">
            <div className="stat__label">کارهای باز</div>
            <div className="stat__value num">{faNum(open)}</div>
          </div>
          <div className="stat">
            <div className="stat__label">مهلتشان گذشته</div>
            <div className="stat__value num">{faNum(late)}</div>
          </div>
          <div className="stat">
            <div className="stat__label">انجام‌شده</div>
            <div className="stat__value num">{faNum(done.length)}</div>
            <div className="stat__basis">
              {all.length - setAside.length > 0
                ? `${faNum(Math.round((done.length / (all.length - setAside.length)) * 100))}٪ از همه`
                : 'هنوز کاری نیست'}
            </div>
          </div>
        </div>
      </div>

      {all.length === 0 ? (
        <div className="card">
          <p className="meta">
            هنوز کاری تعریف نشده. کارها در <Link to="/journey">گفت‌وگو</Link> با افرا تعریف می‌شوند، وقتی روشن
            شد قدم بعدی چیست.
          </p>
        </div>
      ) : (
        <div className="kanban">
          <Column title="در صف" tasks={waiting} empty="کاری در صف نیست" />
          <Column title="در حال انجام" tasks={underway} empty="هنوز کاری شروع نشده" />
          <Column title="انجام‌شده" tasks={done} empty="هنوز کاری بسته نشده" />
        </div>
      )}

      {setAside.length > 0 && (
        <div className="card">
          <button
            type="button"
            className="btn btn--quiet btn--sm"
            aria-expanded={showSetAside}
            onClick={() => setShowSetAside(!showSetAside)}
          >
            کارهای کنار گذاشته <span className="num">({faNum(setAside.length)})</span>
          </button>
          {showSetAside && (
            <div className="kanban__list" style={{ marginTop: 'var(--space-3)' }}>
              {setAside.map((t) => (
                <TaskTile key={t.id} task={t} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Column({ title, tasks, empty }: { title: string; tasks: Task[]; empty: string }) {
  return (
    <section className="kanban__col" aria-label={title}>
      <h2 className="kanban__head">
        <span>{title}</span>
        <span className="num">{faNum(tasks.length)}</span>
      </h2>
      <div className="kanban__list">
        {tasks.length === 0 ? <p className="rail__empty">{empty}</p> : tasks.map((t) => <TaskTile key={t.id} task={t} />)}
      </div>
    </section>
  )
}

type Mode = 'view' | 'edit' | 'claim'

function TaskTile({ task }: { task: Task }) {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<Mode>('view')
  const [error, setError] = useState<string | null>(null)

  const update = useMutation({
    mutationFn: (change: UpdateTaskRequest) => api.updateTask(task.id, change),
    onSuccess: (result) => {
      setError(null)
      setMode('view')
      queryClient.setQueryData(['tasks'], result)
      // The chat panel shows the same tasks.
      void queryClient.invalidateQueries({ queryKey: ['chat-context'] })
    },
    onError: (err) => setError(err instanceof RequestError ? err.messageFa : 'ذخیره نشد. دوباره امتحان کنید.'),
  })

  const isOpen = task.status === 'open'

  return (
    <article className={`task kanban__card ${task.status === 'abandoned' ? 'is-muted' : ''}`}>
      <div style={{ fontWeight: 700 }}>{task.actionFa}</div>
      {task.ownerFa && <div className="meta">مسئول: {task.ownerFa}</div>}
      {task.dueOnFa && <TaskDue task={task} />}
      <div className="task__done">انجام شده یعنی: {task.doneLooksLikeFa}</div>

      {task.lastVerdict && (
        <div style={{ marginTop: 'var(--space-2)' }}>
          <span
            className={`badge ${
              task.lastVerdict === 'accepted' ? 'badge--good' : task.lastVerdict === 'rejected' ? 'badge--bad' : 'badge--warn'
            }`}
          >
            {VERDICT_FA[task.lastVerdict]}
          </span>
          {task.lastModel === null && <span className="badge badge--stored">هنوز بررسی نشده</span>}
          {task.lastReasoningFa && <div className="task__done">{task.lastReasoningFa}</div>}
        </div>
      )}

      {error && <p className="notice notice--error">{error}</p>}

      {mode === 'edit' && (
        <EditForm task={task} pending={update.isPending} onCancel={() => setMode('view')} onSave={(c) => update.mutate(c)} />
      )}
      {mode === 'claim' && <ClaimForm task={task} onDone={() => setMode('view')} />}

      {mode === 'view' && (
        <div className="row kanban__actions">
          {isOpen && task.progress === 'todo' && (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={update.isPending}
              onClick={() => update.mutate({ progress: 'doing' })}
            >
              شروعش کردیم
            </button>
          )}
          {isOpen && (
            <button type="button" className="btn btn--sm" onClick={() => setMode('claim')}>
              انجامش دادیم
            </button>
          )}
          {isOpen && (
            <button type="button" className="btn btn--quiet btn--sm" onClick={() => setMode('edit')}>
              ویرایش
            </button>
          )}
          {isOpen && task.progress === 'doing' && (
            <button
              type="button"
              className="btn btn--quiet btn--sm"
              disabled={update.isPending}
              onClick={() => update.mutate({ progress: 'todo' })}
            >
              برگردد به صف
            </button>
          )}
          {isOpen && (
            <button
              type="button"
              className="btn btn--quiet btn--sm"
              disabled={update.isPending}
              onClick={() => update.mutate({ status: 'abandoned' })}
            >
              کنار بگذار
            </button>
          )}
          {task.status === 'abandoned' && (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={update.isPending}
              onClick={() => update.mutate({ status: 'open' })}
            >
              برگردان به برنامه
            </button>
          )}
        </div>
      )}
    </article>
  )
}

/** Tehran's date `days` from today, in the Persian calendar, for the preview beside the field. */
function jalaliFromToday(days: number): string {
  const date = new Date(Date.now() + days * 86_400_000)
  return new Intl.DateTimeFormat('fa-IR-u-ca-persian', { dateStyle: 'long', timeZone: 'Asia/Tehran' }).format(date)
}

function EditForm({
  task,
  pending,
  onCancel,
  onSave,
}: {
  task: Task
  pending: boolean
  onCancel: () => void
  onSave: (change: UpdateTaskRequest) => void
}) {
  const [action, setAction] = useState(task.actionFa)
  const [owner, setOwner] = useState(task.ownerFa ?? '')
  const [doneLooksLike, setDoneLooksLike] = useState(task.doneLooksLikeFa)
  const [noDeadline, setNoDeadline] = useState(task.dueOn === null)
  const [days, setDays] = useState(String(Math.max(task.daysLeft ?? 14, 0)))
  /* Frozen once a claim was judged against it: the claimant does not get to move the goalposts. */
  const testFrozen = task.claims > 0
  const dayCount = Number(days)
  const daysValid = noDeadline || (Number.isInteger(dayCount) && dayCount >= 0 && dayCount <= 730)

  const save = () => {
    const change: UpdateTaskRequest = {}
    if (action.trim() !== task.actionFa) change.actionFa = action.trim()
    if ((owner.trim() || null) !== task.ownerFa) change.ownerFa = owner.trim() || null
    if (!testFrozen && doneLooksLike.trim() !== task.doneLooksLikeFa) change.doneLooksLikeFa = doneLooksLike.trim()
    if (noDeadline) {
      if (task.dueOn !== null) change.dueInDays = null
    } else if (task.dueOn === null || dayCount !== task.daysLeft) {
      change.dueInDays = dayCount
    }
    if (Object.keys(change).length === 0) onCancel()
    else onSave(change)
  }

  return (
    <div className="stack" style={{ marginTop: 'var(--space-3)' }}>
      <div className="field">
        <label htmlFor={`action-${task.id}`}>چه کاری</label>
        <textarea id={`action-${task.id}`} value={action} onChange={(e) => setAction(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor={`owner-${task.id}`}>چه کسی انجامش می‌دهد</label>
        <input
          id={`owner-${task.id}`}
          value={owner}
          onChange={(e) => setOwner(e.target.value)}
          placeholder="مثلاً: مدیرعامل و مسئول مالی"
        />
      </div>
      <div className="field">
        <label htmlFor={`days-${task.id}`}>مهلت: چند روز دیگر</label>
        <input
          id={`days-${task.id}`}
          className="num"
          inputMode="numeric"
          value={days}
          disabled={noDeadline}
          onChange={(e) => setDays(e.target.value.replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d))))}
        />
        {!noDeadline && daysValid && <span className="meta">یعنی تا {jalaliFromToday(dayCount)}</span>}
        <label className="check check--plain">
          <input type="checkbox" checked={noDeadline} onChange={(e) => setNoDeadline(e.target.checked)} />
          <span>بدون مهلت</span>
        </label>
      </div>
      <div className="field">
        <label htmlFor={`done-${task.id}`}>انجام شده یعنی</label>
        <textarea
          id={`done-${task.id}`}
          value={doneLooksLike}
          disabled={testFrozen}
          onChange={(e) => setDoneLooksLike(e.target.value)}
        />
        {testFrozen && (
          <span className="meta">برای این کار گزارش انجام فرستاده شده، پس این بخش دیگر عوض نمی‌شود.</span>
        )}
      </div>
      <div className="row">
        <button type="button" className="btn btn--sm" disabled={pending || !daysValid || action.trim().length < 3} onClick={save}>
          {pending ? 'در حال ذخیره…' : 'ذخیره'}
        </button>
        <button type="button" className="btn btn--quiet btn--sm" onClick={onCancel}>
          بی‌خیال
        </button>
      </div>
    </div>
  )
}

function ClaimForm({ task, onDone }: { task: Task; onDone: () => void }) {
  const queryClient = useQueryClient()
  const [account, setAccount] = useState('')
  const [error, setError] = useState<string | null>(null)
  const claim = useMutation({
    mutationFn: (accountFa: string) => api.claimTask(task.id, accountFa),
    onSuccess: (result) => {
      setError(null)
      queryClient.setQueryData(['chat-context'], result.context)
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
      onDone()
    },
    onError: (err) => setError(err instanceof RequestError ? err.messageFa : 'ثبت نشد. دوباره امتحان کنید.'),
  })

  return (
    <div style={{ marginTop: 'var(--space-3)' }}>
      <label htmlFor={`claim-${task.id}`}>چه چیزی عوض شد؟ کی انجامش داد و کِی؟</label>
      <textarea
        id={`claim-${task.id}`}
        value={account}
        onChange={(e) => setAccount(e.target.value)}
        placeholder="مثلاً: سوم مهر توی جلسه هیئت‌مدیره تصویب شد و آقای رضایی نوشتش، الان توی گروه واتساپ هست"
      />
      {error && <p className="notice notice--error">{error}</p>}
      <div className="row">
        <button
          type="button"
          className="btn btn--sm"
          disabled={claim.isPending || account.trim().length < 10}
          onClick={() => claim.mutate(account.trim())}
        >
          {claim.isPending ? 'در حال بررسی…' : 'بفرست برای بررسی'}
        </button>
        <button type="button" className="btn btn--quiet btn--sm" onClick={onDone}>
          بی‌خیال
        </button>
      </div>
    </div>
  )
}
